import React, { useEffect, useRef, useState } from "react";

import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CSSTransition, SwitchTransition } from "react-transition-group";

import Banner from "@/components/Banner";
import Footer from "@/components/Footer";
import FormView from "@/components/home/FormView";
import IntroView from "@/components/home/IntroView";
import SuccessView from "@/components/home/SuccessView";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import useLocale from "@/hooks/useLocale";
import { useStore } from "@/stores/index";
import { getNextOrCurrentEvent } from "@/utils/events";

const Index = () => {
  const {} = useTranslation();
  const locale = useLocale();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const store = useStore();

  const [registered, setRegistered] = useState(false);
  const [loadingEvent, setLoadingEvent] = useState<string | null>(null);
  const nodeRef = useRef<HTMLDivElement>(null);

  const [activationFormValues, setActivationFormValues] = useState({
    first_name: "",
    last_name: "",
    address: "",
    email: "",
    postal_code: "",
    province: "",
    marketing_opt_in: false,
  });

  // Determine current view state
  const getCurrentView = () => {
    if (registered) return "success";
    if (store.activation_key && searchParams.get("instance")) return "form";
    return "intro";
  };

  const fetchNextEvent = async (province: string) => {
    if (loadingEvent) return;

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });

    setLoadingEvent(province);

    const nextEvent = await getNextOrCurrentEvent(province);

    if (!nextEvent) {
      setLoadingEvent(null);
      return;
    }

    store.setActivationKey(nextEvent.instance_key);
    navigate(`/${locale}/?instance=${nextEvent.instance_key}`);
    setLoadingEvent(null);
  };

  useEffect(() => {
    if (store.activation_key) return;

    const instance = searchParams.get("instance");
    if (instance) {
      const activationKey =
        instance === "FORD_MODEL_E_DIGITAL_ACTIVATION-BOLLYWOOD"
          ? "FORD_MODEL_E_DIGITAL_ACTIVATION-WATERFRONT_FESTIVAL"
          : instance;
      store.setActivationKey(activationKey);
    }
  }, [searchParams, store.activation_key]);

  const currentView = getCurrentView();

  return (
    <main className="h-full flex-1 flex flex-col">
      <div className="bg-white flex-1 flex flex-col grow h-full">
        <Banner />

        <SwitchTransition>
          <CSSTransition
            key={currentView}
            nodeRef={nodeRef}
            timeout={300}
            classNames="slideIn"
            unmountOnExit
          >
            <div ref={nodeRef}>
              {currentView === "intro" && (
                <IntroView
                  fetchNextEvent={fetchNextEvent}
                  loadingEvent={loadingEvent}
                />
              )}

              {currentView === "form" && (
                <FormView
                  activationFormValues={activationFormValues}
                  setActivationFormValues={setActivationFormValues}
                  setRegistered={setRegistered}
                />
              )}

              {currentView === "success" && <SuccessView />}
            </div>
          </CSSTransition>
        </SwitchTransition>
      </div>
      <Footer />
      <LanguageSwitcher />
    </main>
  );
};

export default Index;
